import { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { CheckCircle } from '@phosphor-icons/react';

interface CompletionStepProps {
  wizardData: any;
  onComplete: () => void;
}

const CompletionStep = ({ wizardData, onComplete }: CompletionStepProps) => {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti after 5 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const brandCount = wizardData.brands?.length || 0;
  const storeCount = wizardData.stores?.length || 0;
  const agentName = wizardData.agent?.definition?.name || 'Your Agent';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '500px',
        textAlign: 'center',
        maxWidth: '768px',
        margin: '0 auto',
        position: 'relative',
      }}
    >
      {/* CSS Confetti Animation */}
      {showConfetti && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {[...Array(50)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: '10px',
                height: '10px',
                backgroundColor: [
                  '#5A4CE6',
                  '#4CAF50',
                  '#F44336',
                  '#FFC107',
                  '#2196F3',
                ][i % 5],
                left: `${Math.random() * 100}%`,
                top: '-10px',
                borderRadius: i % 3 === 0 ? '50%' : '2px',
                animation: `fall ${3 + Math.random() * 2}s linear forwards`,
                animationDelay: `${Math.random() * 2}s`,
                '@keyframes fall': {
                  '0%': {
                    transform: `translateY(0) rotate(0deg)`,
                    opacity: 1,
                  },
                  '100%': {
                    transform: `translateY(100vh) rotate(${
                      360 * (Math.random() > 0.5 ? 1 : -1)
                    }deg)`,
                    opacity: 0,
                  },
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Success icon */}
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'success.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          animation: 'scaleIn 0.5s ease-out',
          zIndex: 2,
          '@keyframes scaleIn': {
            '0%': {
              transform: 'scale(0)',
            },
            '50%': {
              transform: 'scale(1.1)',
            },
            '100%': {
              transform: 'scale(1)',
            },
          },
        }}
      >
        <CheckCircle size={60} weight="fill" color="white" />
      </Box>

      {/* Success heading */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          color: 'custom.textBlue',
          mb: 2,
          fontSize: '24px',
          zIndex: 2,
        }}
      >
        All Done! 🎉
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: 'lightGray.text',
          mb: 4,
          lineHeight: 1.6,
          maxWidth: '500px',
          zIndex: 2,
        }}
      >
        Congratulations! You've successfully completed the setup. Here's what
        you created:
      </Typography>

      {/* Summary box */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'lightGray.main',
          p: 3,
          width: '100%',
          maxWidth: '500px',
          textAlign: 'left',
          mb: 4,
          zIndex: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: 'lightGray.text' }}>
              Brands Created
            </Typography>
            <Typography
              sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main' }}
            >
              {brandCount}
            </Typography>
          </Box>

          <Box sx={{ height: '1px', bgcolor: 'lightGray.main' }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: 'lightGray.text' }}>
              Stores Added
            </Typography>
            <Typography
              sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main' }}
            >
              {storeCount}
            </Typography>
          </Box>

          <Box sx={{ height: '1px', bgcolor: 'lightGray.main' }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: 'lightGray.text' }}>
              Agent Created
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'success.main',
              }}
            >
              "{agentName}"
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* CTA button */}
      <Button
        variant="contained"
        onClick={onComplete}
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          textTransform: 'none',
          borderRadius: '8px',
          height: '40px',
          px: 5,
          py: 1.5,
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: 'none',
          zIndex: 2,
          '&:hover': {
            backgroundColor: 'custom.tertiary',
            boxShadow: 'none',
          },
        }}
      >
        Go to Dashboard
      </Button>
    </Box>
  );
};

export default CompletionStep;
